<template>
  <main class="password-gate">
    <section class="password-gate__card workspace-card" aria-labelledby="reset-title">
      <h1 id="reset-title">Reset your password</h1>
      <p v-if="checking" role="status">Checking your reset link…</p>
      <button v-else-if="token && !validated && !done" class="ui-button" type="button" :disabled="busy" @click="checkLink">Retry link check</button>
      <form v-if="validated && !done" @submit.prevent="save">
        <p>Resetting password for account: <strong>{{ accountName }}</strong></p>
        <p>Choose your own password. Use at least 12 characters, not a common or all-number password.</p>
        <label>New password<input v-model="password" class="ui-input" type="password" minlength="12" maxlength="256" autocomplete="new-password" required /></label>
        <label>Confirm new password<input v-model="confirmation" class="ui-input" type="password" minlength="12" maxlength="256" autocomplete="new-password" required /></label>
        <button class="btn-primary" :disabled="busy" type="submit">Save new password</button>
      </form>
      <template v-else-if="!token && !done">
        <p>Enter your assigned username or the email you have already verified for that account. A new email cannot be bound here.</p>
        <form @submit.prevent="requestLink">
          <label>Username or verified email<input v-model="identifier" class="ui-input" maxlength="254" autocomplete="username" required /></label>
          <button class="btn-primary" :disabled="busy" type="submit">Send reset link</button>
        </form>
      </template>
      <p v-if="message" class="ui-notice" role="status">{{ message }}</p>
      <p><router-link to="/login">Back to login</router-link></p>
      <p><router-link to="/login?help=password">No verified email / ask a teacher for help</router-link></p>
    </section>
  </main>
</template>
<script setup>
import { onMounted, ref } from 'vue'
import { useAuthStore } from '../stores/auth'
import { takeEmailToken, accountRequest } from '../account-email-client'
const auth = useAuthStore()
const token = ref(takeEmailToken()), password = ref(''), confirmation = ref(''), identifier = ref(''), message = ref(''), busy = ref(false), done = ref(false)
const checking = ref(Boolean(token.value)), validated = ref(false), accountName = ref('')
function discardInvalidLink(error) {
  if (error.code === 'invalid_or_expired_link') {
    token.value = ''; validated.value = false; accountName.value = ''; password.value = ''; confirmation.value = ''
  }
}
async function checkLink() {
  checking.value = true; busy.value = true; message.value = ''
  try {
    const data = await accountRequest('auth/password-reset/inspect', { token: token.value })
    accountName.value = data.username; validated.value = true
  } catch (error) { message.value = error.message; discardInvalidLink(error) }
  finally { checking.value = false; busy.value = false }
}
onMounted(() => { if (token.value) checkLink() })
async function save() {
  busy.value = true; message.value = ''
  try {
    await accountRequest('auth/password-reset/confirm', { token: token.value, new_password: password.value, new_password_confirm: confirmation.value })
    token.value = ''; password.value = ''; confirmation.value = ''; done.value = true
    auth.clearLocalSession()
    message.value = 'Password updated. Sign in with your new password.'
  } catch (error) { message.value = error.message; discardInvalidLink(error) }
  finally { busy.value = false }
}
async function requestLink() {
  busy.value = true; message.value = ''
  try { message.value = (await accountRequest('auth/password-reset', { identifier: identifier.value.trim() })).message }
  catch (error) { message.value = error.message }
  finally { busy.value = false }
}
</script>
